import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTheme } from '@/contexts/ThemeContext';
import { API_BASE } from '@/utils/api';
import { DesignPicker } from './DesignPicker';

const ROLES = [['waiter', 'Mozos'], ['cashier', 'Caja'], ['kitchen', 'Cocina']];
const FREE = 'free';
const toSel = (v) => (v === null || v === undefined ? FREE : String(v));

const LockSelect = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const RoleLocks = ({ locks, onChange }) => {
  const set = (role, field, v) => onChange({ ...locks, [role]: { ...(locks[role] || {}), [field]: v === FREE ? null : (field === 'high_contrast' ? v === 'true' : v) } });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 text-xs font-semibold text-muted-foreground"><span>Rol</span><span>Tema</span><span>Alto contraste</span></div>
      {ROLES.map(([role, label]) => (
        <div key={role} className="grid grid-cols-[1fr_1fr_1fr] gap-2 items-center" data-testid={`role-lock-${role}`}>
          <span className="text-sm font-medium">{label}</span>
          <LockSelect value={toSel(locks[role]?.theme)} onChange={(v) => set(role, 'theme', v)} testid={`role-lock-theme-${role}`}
            options={[[FREE, 'Cada uno elige'], ['classic', 'Siempre Clásico'], ['modern', 'Siempre Moderno']]} />
          <LockSelect value={toSel(locks[role]?.high_contrast)} onChange={(v) => set(role, 'high_contrast', v)} testid={`role-lock-hc-${role}`}
            options={[[FREE, 'Cada uno elige'], ['true', 'Siempre encendido'], ['false', 'Siempre apagado']]} />
        </div>
      ))}
    </div>
  );
};

// Tema por defecto del negocio (panel y menú del cliente) y temas fijados por rol
export const BusinessAppearanceCard = () => {
  const { hydrate } = useTheme();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API_BASE}/appearance/business`).then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await axios.put(`${API_BASE}/appearance/business`, data);
      setData(saved);
      hydrate((await axios.get(`${API_BASE}/appearance/me`)).data);
      toast.success('Apariencia del negocio guardada');
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card data-testid="business-appearance-card">
      <CardHeader>
        <CardTitle>Tema del negocio</CardTitle>
        <CardDescription>Lo que ve tu equipo si no eligió otro, y lo que ven tus clientes al escanear el QR.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2"><Label>Panel del personal</Label>
            <DesignPicker value={data.panel_theme} onChange={(v) => setData({ ...data, panel_theme: v })} testid="business-panel-design" /></div>
          <div className="space-y-2"><Label>Menú del cliente (QR)</Label>
            <DesignPicker value={data.menu_theme} onChange={(v) => setData({ ...data, menu_theme: v })} testid="business-menu-design" /></div>
        </div>
        <div className="space-y-2">
          <Label>Fijar por rol</Label>
          <p className="text-xs text-muted-foreground">Si fijás una opción, las personas de ese rol no la pueden cambiar.</p>
          <RoleLocks locks={data.role_locks || {}} onChange={(role_locks) => setData({ ...data, role_locks })} />
        </div>
        <Button onClick={save} disabled={saving} data-testid="business-appearance-save">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar tema del negocio
        </Button>
      </CardContent>
    </Card>
  );
};
